/**
 * ============================================================================
 * [P3-01-D] TextList - 글 목록 컴포넌트
 *
 * 역할:
 * - 글 목록 표시
 * - 무한 스크롤 (hasMore + loadMore)
 * - 로딩/빈 상태 처리
 *
 * NOTE: React 19 호환성을 위해 가상 스크롤 대신 일반 스크롤 사용
 * - 추후 react-window가 React 19 완전 지원 시 가상 스크롤로 변경 가능
 * ============================================================================
 */

import { useCallback, useRef, useEffect } from 'react'
import { TextCard } from './TextCard'
import type { SavedText } from '@/types'
import styles from './Saved.module.css'

// ============================================================================
// Props 타입 정의
// ============================================================================
interface TextListProps {
  texts: SavedText[]
  isLoading: boolean
  hasMore: boolean
  onLoadMore: () => Promise<void>
  onDelete: (id: string) => Promise<boolean>
  onSelect: (text: SavedText) => void
  onToggleFavorite: (id: string) => Promise<boolean>  // [P3-01] 즐겨찾기 토글
  onToggleUsed: (id: string) => Promise<boolean>     // 레퍼런스 사용 상태 토글
  usedReferenceIds?: Set<string>  // 사용된 레퍼런스 ID 집합
}

// ============================================================================
// Constants
// ============================================================================
const LOAD_THRESHOLD = 200 // 스크롤 하단 N px 전에 로드 시작

// ============================================================================
// Component
// ============================================================================
export function TextList({
  texts,
  isLoading,
  hasMore,
  onLoadMore,
  onDelete,
  onSelect,
  onToggleFavorite,
  onToggleUsed,
  usedReferenceIds,
}: TextListProps) {
  // ========================================================================
  // Refs
  // ========================================================================
  const loadingRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // ========================================================================
  // 스크롤 감지 - 추가 로드 트리거
  // ========================================================================
  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    // 스크롤 하단 근처인지 확인
    const { scrollTop, scrollHeight, clientHeight } = container
    const isNearBottom = scrollHeight - scrollTop - clientHeight < LOAD_THRESHOLD

    // 조건: 하단 근처, 더 있음, 로딩 중 아님
    if (isNearBottom && hasMore && !loadingRef.current && !isLoading) {
      loadingRef.current = true
      onLoadMore().finally(() => {
        loadingRef.current = false
      })
    }
  }, [hasMore, isLoading, onLoadMore])

  // ========================================================================
  // 스크롤 이벤트 리스너
  // ========================================================================
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  // ========================================================================
  // 빈 상태 처리
  // ========================================================================
  if (!isLoading && texts.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>저장된 글이 없습니다.</p>
        <p>글 작성 탭에서 새 글을 작성해보세요!</p>
      </div>
    )
  }

  // ========================================================================
  // Render
  // ========================================================================
  return (
    <div ref={containerRef} className={styles.textList}>
      {/* 글 목록 */}
      {texts.map((text) => (
        <TextCard
          key={text.id}
          text={text}
          onDelete={onDelete}
          onSelect={onSelect}
          onToggleFavorite={onToggleFavorite}
          onToggleUsed={onToggleUsed}
          isUsed={
            text.type === 'reference'
              ? text.isUsed || usedReferenceIds?.has(text.id)
              : text.type === 'edit'
                ? (text.isUsed ?? false)
                : undefined
          }
        />
      ))}

      {/* 로딩 인디케이터 */}
      {isLoading && (
        <div className={styles.loadingIndicator}>
          <span className={styles.loadingSpinner} />
          <span>불러오는 중...</span>
        </div>
      )}

      {/* 더 이상 데이터가 없을 때 */}
      {!hasMore && texts.length > 0 && (
        <div className={styles.loadingIndicator}>
          <span>모든 글을 불러왔습니다.</span>
        </div>
      )}
    </div>
  )
}

export default TextList
